import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/use-user-role";
import { CoachCalendar } from "@/components/CoachCalendar";
import { CoachAnalytics } from "@/components/CoachAnalytics";
import { BlockedTimeManager } from "@/components/BlockedTimeManager";
import { BookingRequestCard } from "@/components/BookingRequestCard";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, BarChart3, LogOut, Ban, Inbox, User as UserIcon, Menu } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/logo.svg";

interface Booking {
  id: string;
  booking_datetime: string; // UTC ISO timestamp
  booking_timezone?: string;
  proposed_datetime?: string; // UTC ISO timestamp
  duration_minutes: number;
  status: string;
  price: number;
  user_id: string;
  coach_id?: string;
  video_conference_link?: string;
  denied_by?: string[];
  _isPast?: boolean;
  profiles?: {
    full_name: string;
    email: string;
  };
}

const CoachPortal = () => {
  const navigate = useNavigate();
  const { isCoach, loading: roleLoading } = useUserRole();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!roleLoading && !isCoach) {
      toast.error("Access denied. This page is for coaches only.");
      navigate("/");
    }
  }, [isCoach, roleLoading, navigate]);

  useEffect(() => {
    if (isCoach) {
      fetchUserId();
      fetchBookings();
    }
  }, [isCoach]);

  const fetchUserId = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) setUserId(user.id);
  };

  const fetchBookings = async () => {
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select(`
          *,
          profiles:user_id (
            full_name,
            email
          )
        `)
        .order("booking_datetime", { ascending: true });

      if (error) throw error;
      
      // Filter out past pending bookings from the inbox view
      const now = new Date();
      const filteredBookings = (data || []).map(booking => {
        // Check if booking is past and still pending/unassigned
        if (!booking.coach_id && booking.status === "pending") {
          const bookingDateTime = new Date(booking.booking_datetime);
          // Hide past pending bookings that are more than 1 hour old
          if (bookingDateTime < new Date(now.getTime() - 60 * 60 * 1000)) {
            return { ...booking, _isPast: true };
          }
        }
        return booking;
      });
      
      setBookings(filteredBookings);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  if (roleLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-accent/20">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 md:gap-3">
            <img src={logo} alt="Vocalzing logo" className="h-8 w-8 md:h-10 md:w-10" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold">Coach Portal</h1>
              <p className="text-xs md:text-sm text-muted-foreground hidden sm:block">Manage your vocal lessons</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-2">
            <Button onClick={() => navigate("/profile")} variant="ghost" size="sm">
              <UserIcon className="h-4 w-4 mr-2" />
              Profile
            </Button>
            <Button onClick={handleSignOut} variant="outline" size="sm">
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>

          {/* Mobile Hamburger Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="sm">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <div className="flex flex-col gap-4 mt-8">
                <Button 
                  onClick={() => {
                    navigate("/profile");
                    setMobileMenuOpen(false);
                  }} 
                  variant="ghost" 
                  className="w-full justify-start"
                >
                  <UserIcon className="h-4 w-4 mr-2" />
                  Profile
                </Button>
                <Button 
                  onClick={() => {
                    handleSignOut();
                    setMobileMenuOpen(false);
                  }} 
                  variant="outline"
                  className="w-full justify-start"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="inbox" className="space-y-6">
          <TabsList>
            <TabsTrigger value="inbox">
              <Inbox className="w-4 h-4 mr-2" />
              Inbox
              {bookings.filter(b => b.coach_id === userId && b.status === "pending" && !b._isPast).length > 0 && (
                <Badge className="ml-2" variant="destructive">
                  {bookings.filter(b => b.coach_id === userId && b.status === "pending" && !b._isPast).length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="calendar">
              <Calendar className="w-4 h-4 mr-2" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="blocked">
              <Ban className="w-4 h-4 mr-2" />
              Block Time
            </TabsTrigger>
            <TabsTrigger value="analytics">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inbox">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Booking Requests</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Accept or deny booking requests. Accepted bookings will appear on your calendar.
                </p>
              </div>
              {bookings.filter(b => b.coach_id === userId && b.status === "pending" && !b._isPast).length === 0 ? (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">No pending booking requests</p>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {bookings
                    .filter(b => b.coach_id === userId && b.status === "pending" && !b._isPast)
                    .map((booking) => (
                      <BookingRequestCard
                        key={booking.id}
                        booking={booking}
                        showActions={true}
                        onUpdate={fetchBookings}
                      />
                    ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="calendar">
            <CoachCalendar 
              bookings={bookings.filter(b => b.coach_id === userId)} 
              onUpdate={fetchBookings} 
            />
          </TabsContent>

          <TabsContent value="blocked">
            <BlockedTimeManager />
          </TabsContent>

          <TabsContent value="analytics">
            <CoachAnalytics bookings={bookings.filter(b => b.coach_id === userId)} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default CoachPortal;
